import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { auth, db } from './firebase'; // Add db import
import { signOut as firebaseSignOut } from "firebase/auth"; // Remove Google auth imports
import { doc, getDoc } from "firebase/firestore"; // Add Firestore imports
import './NavigationBar.css'; // Import the new CSS file
import { usePostHog } from 'posthog-js/react';
import { useAdminView } from './contexts/AdminViewContext'; // Use admin view context

// Strava OAuth Configuration
const STRAVA_CLIENT_ID = "159733";
const STRAVA_SIGNIN_REDIRECT_URI = window.location.origin + "/strava-auth-callback";
const STRAVA_SCOPE = "activity:read,activity:read_all,activity:write";

function NavigationBar({ user }) {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { isAdmin, actualIsAdmin, isAdminViewMode } = useAdminView(); // Use context
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [badgesVisibleToAll, setBadgesVisibleToAll] = useState(false);
    const posthog = usePostHog();

    const handleSignOut = async () => {
        posthog?.capture('user_signed_out', {
            page: 'navigation'
        });
        
        try {
            await firebaseSignOut(auth);
            setIsMobileMenuOpen(false); // Close menu on sign out
            navigate('/'); // Navigate to home after sign out
            posthog?.capture('user_sign_out_success');
        } catch (error) {
            posthog?.capture('user_sign_out_failed', {
                error_message: error.message
            });
            alert(`Sign-out error: ${error.message}`);
        }
    };

    // Fetch badge visibility setting when user is authenticated
    useEffect(() => {
        const fetchBadgeVisibility = async () => {
            // Only fetch if user is authenticated
            if (!user) {
                setBadgesVisibleToAll(false);
                return;
            }
            
            try {
                const settingsDoc = await getDoc(doc(db, 'settings', 'mapGeneration'));
                if (settingsDoc.exists()) {
                    const data = settingsDoc.data();
                    setBadgesVisibleToAll(data.badgesVisibleToAll || false);
                }
            } catch (error) {
                console.error('Error fetching badge visibility setting:', error);
                // Default to false if there's an error
                setBadgesVisibleToAll(false);
            }
        };
        
        fetchBadgeVisibility();
    }, [user]); // Re-run when user changes

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    // Track navigation clicks
    const handleNavClick = (destination) => {
        posthog?.capture('navigation_clicked', {
            destination: destination,
            current_page: window.location.pathname
        });
    };

    return (
        <nav className="navbar">
            <div className="navbar-logo-links">
                <Link to="/" className="navbar-brand" onClick={closeMobileMenu}>Mythic Maps</Link>
                {/* Desktop Links: Hidden on mobile by default, shown via media query */}
                <div className={`navbar-links ${isMobileMenuOpen ? 'open' : ''}`}>
                    {user && <NavLink to="/history" className="nav-link" onClick={closeMobileMenu}>{t('nav.history')}</NavLink>}
                    {user && <NavLink to="/settings" className="nav-link" onClick={closeMobileMenu}>{t('nav.settings')}</NavLink>}
                    {user && <NavLink to="/credits" className="nav-link" onClick={closeMobileMenu}>{t('nav.credits')}</NavLink>}
                    {user && (isAdmin || badgesVisibleToAll) && <NavLink to="/badges" className="nav-link" onClick={closeMobileMenu}>{t('nav.badges')}</NavLink>}
                    {isAdmin && <NavLink to="/gallery" className="nav-link" onClick={closeMobileMenu}>{t('nav.gallery')}</NavLink>}
                    {isAdmin && <NavLink to="/generate/strava/_new" className="nav-link" onClick={closeMobileMenu}>{t('nav.generateNew')}</NavLink>}
                    {isAdmin && <NavLink to="/admin/dashboard" className="nav-link" onClick={closeMobileMenu}>{t('nav.dashboard')}</NavLink>}
                    {isAdmin && <NavLink to="/admin/test-comparisons" className="nav-link" onClick={closeMobileMenu}>{t('nav.testComparisons')}</NavLink>}
                    {isAdmin && <NavLink to="/admin/prompts" className="nav-link" onClick={closeMobileMenu}>{t('nav.managePrompts')}</NavLink>}
                    {isAdmin && <NavLink to="/admin/strava-webhook" className="nav-link" onClick={closeMobileMenu}>{t('nav.stravaWebhooks')}</NavLink>}
                    {isAdmin && <NavLink to="/admin/badges" className="nav-link" onClick={closeMobileMenu}>{t('nav.badgeManager')}</NavLink>}
                </div>
            </div>

            <div className="navbar-actions">
                {user ? (
                    <>
                        <span className="navbar-user-info">{user.displayName || user.email}</span>
                        {actualIsAdmin && (
                            <span className="admin-badge" style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '4px',
                                opacity: isAdminViewMode ? 1 : 0.6 
                            }}>
                                Admin
                                {!isAdminViewMode && <span style={{ fontSize: '10px' }}>(User View)</span>}
                            </span>
                        )}
                        <button onClick={handleSignOut} className="navbar-button">{t('nav.signOut')}</button>
                    </>
                ) : null}
                <select
                    aria-label="Language"
                    value={i18n.language}
                    onChange={(e) => i18n.changeLanguage(e.target.value)}
                    className="navbar-button"
                    style={{ marginLeft: '8px' }}
                >
                    <option value="en">English</option>
                    <option value="zh-CN">简体中文</option>
                </select>
                <button className={`hamburger-menu ${isMobileMenuOpen ? 'open' : ''}`} onClick={toggleMobileMenu} aria-label="Toggle menu">
                    <span className="bar"></span>
                    <span className="bar"></span>
                    <span className="bar"></span>
                </button>
            </div>
        </nav>
    );
}

export default NavigationBar; 